import * as React from 'react';
import styled, { ThemeContext, css, keyframes } from 'styled-components';
import Color from 'color';
import { PtMuiIconProps } from '@floji-internal/mui-icons';
import { AvatarSizeName, ColorSet, PaletteGroups, getAvatarSize } from "../../theme"


type Modify<T, R> = Omit<T, keyof R> & R;

type StyleProps = {
    size: number,
    colorSet: ColorSet,
    checked?: boolean,
    pulse?: boolean
}
type BaseProps = {
    size?: AvatarSizeName,
    color?: ColorSet | keyof PaletteGroups
    icon: React.ReactElement<PtMuiIconProps>
    uncheckedIcon?: React.ReactElement<PtMuiIconProps>
    rotate?: number
    children?: never
    iconSizeOverride?: AvatarSizeName
}
type ButtonVariantProps = BaseProps & {
    variant?: "solid" | "outline" | "icon" | "translucent"
    onChange?: never
    checked?: never,
    pulse?: boolean
}
type ToggleVariantProps = BaseProps & {
    variant?: "toggle"
    onChange?: (checked: boolean, e: React.MouseEvent<HTMLButtonElement>) => void
    checked?: boolean,
    pulse?: boolean
}
type IconButtonProps = Modify<React.ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariantProps | ToggleVariantProps>

const pulseAnimation = keyframes`
    // 0.5 0.25 0.5 2.0 
    // Not quite but close enough to uk ring
    0%  { opacity:1 ;}
    50%  { opacity:0.3;}
    100%  { opacity:1 ;}  
`;


const IconButtonBaseStyles = css<StyleProps>`
    /* Button Style Resets */    
    border: none;
    cursor: pointer;  
    outline: none;
    border-radius: 50%;
    position: relative;
    ${p => p.pulse
        ? css`animation: ${pulseAnimation} 2000ms linear infinite;`
        : undefined
    }
    transition: 
        background ${props => props.theme.transitions.short},
        color ${props => props.theme.transitions.short},
        opacity ${props => props.theme.transitions.short};

    /* position the icon */
    > *:first-child {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`;

// Default also covers the toggle variant
const DefaultIconButton = styled.button<StyleProps>`
    ${IconButtonBaseStyles}

    /* Size */
    width: ${props => props.size}px;
    height: ${props => props.size}px;

    /* Color */
    background: ${props => props.checked ?
        Color(props.colorSet.main).alpha(0.2).toString() :
        "transparent"};
    color: ${props => props.checked ?
        props.colorSet.darker :
        props.colorSet.main};
    
    :hover:not(:disabled) {
        background:${props => props.checked ?
        Color(props.colorSet.main).alpha(0.3).toString() :
        Color(props.colorSet.main).alpha(0.2).toString()};
        color: ${props => props.colorSet.darker};
    }
    
    :active:not(:disabled) {
        background:${props => props.checked ?
        Color(props.colorSet.main).alpha(0.4).toString() :
        Color(props.colorSet.main).alpha(0.3).toString()};
        color: ${props => props.colorSet.darker};
    }
    
    :focus-visible:not(:disabled):not(:hover):not(:active) {
        background:${props => props.checked ?
        Color(props.colorSet.main).alpha(0.3).toString() :
        Color(props.colorSet.main).alpha(0.2).toString()};
        color: ${props => props.colorSet.darker};
    }
    
    :disabled {
        opacity: 0.5;
        cursor: cursor;
    } 
`;

const SolidIconButton = styled.button<StyleProps>`
    ${IconButtonBaseStyles}
    
    /* Size */
    width: ${props => props.size}px;
    height: ${props => props.size}px;

    /* Color */
    box-shadow: ${props => props.theme.shadows.inset};
    background: ${props => props.colorSet.main};
    color: ${props => props.colorSet.contrast};
    :hover:not(:disabled) {
         background:${props => props.colorSet.darker}
    }
    :active:not(:disabled) {
        background:${props => props.colorSet.lighter}
    }
    :focus-visible:not(:disabled) {
        box-shadow: ${props => props.theme.shadows.inset}, 0 0 4px 0 ${props => Color(props.colorSet.main).alpha(0.85).toString()};
    }
    :disabled {
        opacity: 0.5;
        cursor: cursor;
    } 
`;

const OutlineIconButton = styled.button<StyleProps>`
    ${IconButtonBaseStyles}

    /* Size */
    width: ${props => props.size}px;
    height: ${props => props.size}px;

    /* Color */
    background: transparent;
    color: ${props => props.colorSet.main};
    border: 1px solid ${props => Color(props.colorSet.main).alpha(0.4).toString()};
    :hover:not(:disabled) {
        background:${props => Color(props.colorSet.main).alpha(0.1).toString()};
        border: 1px solid ${props => Color(props.colorSet.darker).alpha(0.5).toString()};
        color: ${props => props.colorSet.darker};
    }
    :active:not(:disabled) {
        background:${props => Color(props.colorSet.main).alpha(0.2).toString()};
        border: 1px solid ${props => Color(props.colorSet.darker).alpha(0.5).toString()};
        color: ${props => props.colorSet.darker};
    }
    :focus:not(:disabled):not(:hover):not(:active) {
        background:${props => Color(props.colorSet.main).alpha(0.1).toString()};
    }
    :disabled {
        opacity: 0.5;
        cursor: cursor;
    } 
`;

// $CB: TranslucentButton only really works in the Dark theme. 
//      If this is going to be used more often may need more consideration.
const TranslucentButton = styled.button<StyleProps>`
    ${IconButtonBaseStyles}

    /* Size */
    width: ${props => props.size}px;
    height: ${props => props.size}px;

    /* Color */
    background: ${props => props.theme.palette.ui.scrim};
    color: ${props => props.theme.palette.text.primary};
    backdrop-filter: blur(${props => props.theme.blur});
    
    :hover:not(:disabled) {
        color: ${props => Color(props.theme.palette.text.primary).alpha(1).toString()};
        background:${props => Color(props.theme.palette.ui.scrim).alpha(0.4).toString()};
    }
    
    :active:not(:disabled) {
        color: ${props => Color(props.theme.palette.text.primary).alpha(1).toString()};
        background:${props => Color(props.theme.palette.ui.scrim).alpha(0.5).toString()};
    }
    
    :focus-visible:not(:disabled):not(:hover):not(:active) {
        color: ${props => Color(props.theme.palette.text.primary).alpha(1).toString()};
        background:${props => Color(props.theme.palette.ui.scrim).alpha(0.4).toString()};
    }
    
    :disabled {
        opacity: 0.5;
        cursor: cursor;
    } 
`;



const IconButton = React.forwardRef((props: IconButtonProps, ref: React.Ref<HTMLButtonElement>) => {
    const { color, variant, size, iconSizeOverride, icon: checkedIcon, uncheckedIcon, children, rotate, checked: checkedProp, onChange, onClick, ...buttonProps } = props
    const theme = React.useContext(ThemeContext);

    let Component;
    switch (variant) {
        case "solid": Component = SolidIconButton; break;
        case "outline": Component = OutlineIconButton; break;
        case "translucent": Component = TranslucentButton; break;
        default: Component = DefaultIconButton; break;
    }

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (variant == "toggle") {
            onChange && onChange(!props.checked, e);
        }
        onClick && onClick(e);
    }

    let colorSet: ColorSet;
    if (typeof color === "object") colorSet = color;
    else if (!color) colorSet = theme.palette.primary;
    else colorSet = theme.palette[color];

    let pxSize: number = getAvatarSize(size);
    let icoSize: number = getAvatarSize(iconSizeOverride);
    if (!iconSizeOverride) {
        switch (size) {
            case "xLarge": icoSize = 28; break;
            case "large": icoSize = 24; break;  // Large is supposed to have the same size icon as medium
            case "medium": icoSize = 24; break;
            case "small": icoSize = 20; break;
            case "tiny": icoSize = 16; break;
            default: icoSize = 24; break;
        }
    }

    let icon = (props.checked) ? checkedIcon : uncheckedIcon || checkedIcon; // choose the correct icon for the state, if no uncheckedIcon provided go with Icon.
    let IconComponent = React.cloneElement(icon, { size: `${icoSize}px` });

    return <Component
        pulse={props.pulse}
        style={{ transform: `rotate(${rotate}deg)` }}
        size={pxSize}
        colorSet={colorSet}
        onClick={handleClick}
        checked={props.checked}
        ref={ref}
        {...buttonProps}
        type={props.type || "button"}>
        {children || IconComponent}
    </Component>
})


export default IconButton;
export { IconButton, IconButtonProps }

